// engine.pattern.ts
// Pattern Compiler and Cache Engine for Tempo
// Responsible for snippet/layout expansion, regex compilation, and pattern caching
import { isRegExp, isNullish, isEmpty, isString } from '#library/assertion.library.js';
import { WeakCache } from '#library/weakcache.class.js';
import { asArray } from '#library/coercion.library.js';
import { ownEntries, ownKeys, hasOwn } from '#library/primitive.library.js';
import { Enum } from '#library/enumerate.library.js';
import { Match, Layout } from '../support/support.default.js';
import { getSymbol, logWarn, logError } from '../support/support.util.js';
import { Token } from '../support/support.symbol.js';
import enums from '../support/support.enum.js';
const BRACES_REGEX = new RegExp(Match.braces, 'g');
const RE_NAMED_CAPTURE_GROUP = /\(\?<([a-zA-Z][\w]*)>/g;
const RE_NON_IDENTIFIER_CHARS = /[^A-Za-z0-9_$]/g;
const RE_IDENTIFIER_START = /^[A-Za-z_$]/;
export class PatternCompiler {
    #state;
    #globalCache = new WeakCache();
    #snippetCache = new WeakMap();
    constructor(options) {
        this.#state = options.state;
    }
    get state() { return this.#state; }
    /**
     * Translates {layout} into an anchored, case-insensitive RegExp.
     * Includes recursive expansion of placeholders using snippet registries.
     */
    compileRegExp(layout, snippet) {
        const state = this.#state;
        const source = isRegExp(layout) ? layout.source : layout;
        let cache;
        if (snippet) {
            if (!this.#snippetCache.has(snippet))
                this.#snippetCache.set(snippet, new WeakCache());
            cache = this.#snippetCache.get(snippet);
        }
        else {
            cache = this.#globalCache;
        }
        if (cache.has(source))
            return cache.get(source);
        const matcher = (source, d = 0) => {
            if (d > 10) { // Emit a diagnostic if recursion limit is hit (likely circular placeholder)
                logWarn(`[PatternCompiler] Recursion limit exceeded in matcher (d > 10) for src:`, this.#state.config, source, `depth:`, d);
                return source;
            }
            if (source.length >= 2 && source.startsWith('/') && source.endsWith('/'))
                source = source.slice(1, -1); // remove the leading/trailing "/"
            if (source.startsWith('^') && source.endsWith('$'))
                source = source.slice(1, -1); // remove the leading/trailing anchors (^ $)
            return source.replace(BRACES_REGEX, (match, name) => {
                const token = getSymbol(name); // get the symbol for this {name}
                const customs = snippet?.[token]?.source ?? snippet?.[name]?.source;
                const globals = state.parse.snippet[token]?.source ?? state.parse.snippet[name]?.source;
                const stateLayout = state.parse.layout[token] ?? state.parse.layout[name];
                const defaultLayout = Layout[token]; // get resolution source (layout)
                let res = customs ?? globals ?? stateLayout ?? defaultLayout; // get the snippet/layout source
                if (isNullish(res) && (name === 'tzd' || token === Token.tzd)) {
                    res = snippet?.[Token.tz]?.source ?? snippet?.['tz']?.source
                        ?? state.parse.snippet[Token.tz]?.source ?? state.parse.snippet['tz']?.source;
                }
                else if (isNullish(res) && (name === 'tz' || token === Token.tz)) {
                    res = snippet?.[Token.tzd]?.source ?? snippet?.['tzd']?.source
                        ?? state.parse.snippet[Token.tzd]?.source ?? state.parse.snippet['tzd']?.source;
                }
                if (isNullish(res) && name.includes('.')) { // if no definition found, try fallback
                    const prefix = name.split('.')[0]; // get the base token name
                    const pToken = getSymbol(prefix);
                    res = snippet?.[pToken]?.source ?? snippet?.[prefix]?.source
                        ?? state.parse.snippet[pToken]?.source ?? state.parse.snippet[prefix]?.source
                        ?? state.parse.layout[pToken] ?? state.parse.layout[prefix]
                        ?? Layout[pToken];
                }
                if (res && name.includes('.')) { // wrap dotted extensions for identification
                    let safeName = name.trim().replace(RE_NON_IDENTIFIER_CHARS, '_');
                    if (!RE_IDENTIFIER_START.test(safeName))
                        safeName = `_${safeName}`;
                    if (!res.startsWith(`(?<${safeName}>`))
                        res = `(?<${safeName}>${res})`;
                }
                return (isNullish(res) || res === match) // if no definition found,
                    ? match // return the original match
                    : matcher(res, d + 1); // else recurse to see if snippet contains embedded "{}" pairs
            });
        };
        try {
            const expanded = deduplicateCaptureGroups(matcher(source));
            const compiled = new RegExp(`^(${expanded})$`, 'i');
            cache.set(source, compiled);
            return compiled;
        }
        catch (e) {
            // Use the computed source for fallback, do not cache fallback, and log error
            logError(e, this.#state.config, { context: 'pattern compile failed', pattern: source });
            return new RegExp(`^${Match.escape(source)}$`, 'i');
        }
    }
    /**
     * Build RegExp patterns into the state.
     * Re-evaluates all snippets and layouts.
     */
    setPatterns() {
        this.clearCache();
        const state = this.#state;
        // ensure we have our own isolated mutable containers before mutation
        state.parse.snippet = { ...state.parse.snippet };
        state.parse.pattern = new Map();
        const snippet = state.parse.snippet;
        // 1. ensure numeric snippets are current
        if (enums?.NUMBER) {
            const localNumbers = state.config.registry?.numbers ?? {};
            const keys = Array.from(new Set([
                ...Enum.keys(enums.NUMBER),
                ...Object.keys(localNumbers)
            ])).map((w) => Match.escape(w));
            const nbr = new RegExp(`(?<nbr>[0-9]+|${keys.sort((a, b) => b.length - a.length).join('|')})`);
            snippet[Token.nbr] = nbr;
            let modPattern = Match.modifier.source;
            let afxPattern = Match.modifier.source;
            if (state.config.registry?.modifiers) {
                const symbols = ['+', '-', '<', '<=', '>', '>=', '='];
                const words = new Set();
                symbols.forEach(sym => {
                    asArray(state.config.registry.modifiers[sym])
                        .forEach(w => words.add(w));
                });
                if (words.size > 0) {
                    const escapedWords = Array.from(words).map(w => Match.escape(w));
                    escapedWords.sort((a, b) => b.length - a.length);
                    const wordPattern = escapedWords.join('|');
                    modPattern = `[\\+\\-\\<\\>][\\=]?|${wordPattern}`;
                    afxPattern = `[\\+\\-\\<\\>][\\=]?|${wordPattern}`;
                    const op = `[\\+\\-\\<\\>]=?`;
                    const terms = `(?:#[\\w]+|[\\w]+)`;
                    const units = `(?:[\\w]*)`;
                    snippet[Token.slk] = new RegExp(`(?:${terms}\\.(?:${op}|${wordPattern})?(?:[0-9]+)?${units})`, 'i');
                }
            }
            snippet[Token.mod] = new RegExp(`((?<mod>${modPattern})? *)`, 'i');
            snippet[Token.afx] = new RegExp(`((s)? (?<afx>${afxPattern}))?${snippet[Token.sep]?.source || ''}?`, 'i');
        }
        if (enums?.ORDINAL) {
            const localOrdinals = state.config.registry?.ordinals ?? {};
            const ordKeys = Array.from(new Set([
                ...Enum.keys(enums.ORDINAL),
                ...Object.keys(localOrdinals)
            ])).map((w) => Match.escape(w));
            const ordWordPattern = ordKeys.sort((a, b) => b.length - a.length).join('|');
            snippet[Token.nth] = new RegExp(`(?<nth>(?:[0-9]{0,2}11th|[0-9]{0,2}12th|[0-9]{0,2}13th|[0-9]{0,2}[04-9]th|[0-9]{0,2}[02-9]1st|1st|[0-9]{0,2}[02-9]2nd|2nd|[0-9]{0,2}[02-9]3rd|3rd|[1-9][0-9]{0,2})|${ordWordPattern})`, 'i');
        }
        // 2. build ignore pattern
        const ignores = ownKeys(state.parse.ignore, true);
        if (!isEmpty(ignores)) {
            const words = ignores
                .filter(isString)
                .map(w => Match.escape(w.toLowerCase()))
                .join('|');
            state.parse.ignorePattern = new RegExp(`\\b(${words})\\b`, 'gi');
        }
        else {
            delete state.parse.ignorePattern;
        }
        // 3. build the patterns
        ownEntries(state.parse.layout, true).forEach(([key, layout]) => {
            const symbol = getSymbol(key);
            const compiled = this.compileRegExp(layout, snippet);
            state.parse.pattern.set(symbol, compiled);
        });
    }
    /**
     * Clear the pattern cache.
     */
    clearCache() {
        this.#globalCache.clear();
        // WeakMap has no clear(), so re-instantiate to drop all snippet-specific caches
        this.#snippetCache = new WeakMap();
    }
}
/**
 * Functional wrapper for the PatternCompiler.
 * Handles engine instantiation and pattern building for a given state.
 */
export function setPatterns(state) {
    // 🛡️ Critical fix: ensure we use an OWN PatternCompiler for each state to avoid cross-pollution
    if (!hasOwn(state, 'patternCompiler') || !state.patternCompiler)
        state.patternCompiler = new PatternCompiler({ state });
    state.patternCompiler.setPatterns();
}
/**
 * Deduplicates named capture groups across regex '|' branches to avoid SyntaxError in JS RegExp compilation.
 */
function deduplicateCaptureGroups(expanded) {
    if (!expanded.includes('|') || !expanded.includes('(?<'))
        return expanded;
    const reserved = new Set();
    const matches = expanded.matchAll(RE_NAMED_CAPTURE_GROUP);
    for (const m of matches)
        reserved.add(m[1]);
    const counts = new Map();
    return expanded.replace(RE_NAMED_CAPTURE_GROUP, (match, name) => {
        const count = counts.get(name) ?? 0;
        counts.set(name, count + 1);
        if (count === 0)
            return match;
        let altSuffix = count;
        let altName = `${name}_alt${altSuffix}`;
        while (reserved.has(altName)) {
            altSuffix++;
            altName = `${name}_alt${altSuffix}`;
        }
        reserved.add(altName);
        return `(?<${altName}>`;
    });
}
